import { isSameDay } from 'date-fns';

import { calendarResourceTypes } from '../../../components/general';
import { PlanningWarning } from '../../../components/general/PlanningWarningDialog.tsx';
import { CalendarEvent } from '../../../components/general/ResourceCalendar';
import { CalendarRaumEvent } from '../../../components/general/ResourceCalendar/helperClasses';
import { calendarEventStates } from '../../../components/general/ResourceCalendar/helperClasses/CalendarEvent/calendarEventStates.ts';
import {
  CheckDurchgangAvailabilityOutput,
  CheckDurchgangAvailabilityOutputModulTag,
  CreateDurchgangInputBlockung,
  ModulFromThemaRouter,
  ressourcenBlockungTypes,
  Zeitraum,
} from '../../../dtos.ts';
import { formatList } from '../../../utils/dateFormat/listFormat.ts';

export const getAppliedModulterminChanges = (
  calendarEvents: readonly CalendarEvent[],
  ablauf: Zeitraum[],
  modulnummer: number | undefined,
  prevPlanResultData: CheckDurchgangAvailabilityOutput,
): CheckDurchgangAvailabilityOutput | undefined => {
  if (!modulnummer) {
    return;
  }

  // set planResultData tage to days of ablauf if no events are present
  if (calendarEvents.length === 0) {
    return {
      ...prevPlanResultData,
      module: prevPlanResultData.module.map((modul) => {
        if (modul.modulnummer !== modulnummer) {
          return modul;
        }

        return {
          ...modul,
          tage: ablauf.map((tagZeitraum) => ({
            zeitraum: tagZeitraum,
            expertenSapIds: [],
          })),
          seminarraum: undefined,
        };
      }),
    };
  }

  // return old data if Modul can not be found
  const prevModul = prevPlanResultData.module.find((m) => m.modulnummer === modulnummer);
  if (!prevModul) {
    return prevPlanResultData;
  }

  const foundGruppenraumEvents = calendarEvents.filter(
    (event): event is CalendarRaumEvent => event.type === calendarResourceTypes.GRUPPENRAUM,
  );

  const updatedModulTage: CheckDurchgangAvailabilityOutputModulTag[] = ablauf.map((tagZeitraum) => ({
    zeitraum: tagZeitraum,
    expertenSapIds: calendarEvents
      .filter(
        (event) =>
          event.type === calendarResourceTypes.EXPERTE &&
          isSameDay(event.start, tagZeitraum.start) &&
          isSameDay(event.end, tagZeitraum.end),
      )
      .map((event) => event.ressourceSapId),
    gruppenraeume: foundGruppenraumEvents
      .filter((event) => isSameDay(event.start, tagZeitraum.start) && isSameDay(event.end, tagZeitraum.end))
      .map((event) => ({
        raumSapId: event.ressourceSapId,
        kuerzel: event.raumKuerzel,
        available: event.state !== calendarEventStates.ERROR,
      })),
  }));

  const allExpertenAvailable = calendarEvents
    .filter((event) => event.type === calendarResourceTypes.EXPERTE)
    .every((event) => event.state !== calendarEventStates.ERROR);

  const foundSeminarraumEvent = calendarEvents.find(
    (event): event is CalendarRaumEvent => event.type === calendarResourceTypes.SEMINARRAUM,
  );
  const seminarraum = foundSeminarraumEvent
    ? {
        raumSapId: foundSeminarraumEvent.ressourceSapId,
        kuerzel: foundSeminarraumEvent.raumKuerzel,
        available: foundSeminarraumEvent.state !== calendarEventStates.ERROR,
      }
    : undefined;

  const updatedModul: CheckDurchgangAvailabilityOutput['module'][number] = {
    modulnummer,
    gruppenraumInfo: prevModul.gruppenraumInfo,
    tage: updatedModulTage,
    seminarraum,
    available: allExpertenAvailable,
  };

  const updatedModule: CheckDurchgangAvailabilityOutput['module'] = prevPlanResultData.module.map((modul) =>
    modul.modulnummer === modulnummer ? updatedModul : modul,
  );

  return {
    ...prevPlanResultData,
    module: updatedModule,
  };
};

const getAkaModulIdByModulnummer = (themaModule: ModulFromThemaRouter[], modulnummer: number) => {
  const foundModul = themaModule.find((m) => m.modulnummer === modulnummer);
  if (foundModul) {
    return foundModul.akaModulId;
  } else {
    throw new Error(`Modul mit der Nummer ${modulnummer} nicht gefunden.`);
  }
};

export const getSaveInputData = (
  planResultData: CheckDurchgangAvailabilityOutput,
  themaModule: ModulFromThemaRouter[],
) => {
  const getModulBlockungen = (
    modul: CheckDurchgangAvailabilityOutput['module'][number],
  ): CreateDurchgangInputBlockung[] => {
    const blockungen: CreateDurchgangInputBlockung[] = [];

    modul.tage.flatMap((tag) => {
      tag.expertenSapIds.forEach((experteSapId) => {
        blockungen.push({
          type: ressourcenBlockungTypes.EXPERTE,
          ressourceSapId: experteSapId,
          start: tag.zeitraum.start,
          end: tag.zeitraum.end,
        });
      });

      tag.gruppenraeume?.forEach((gruppenraum) => {
        blockungen.push({
          type: ressourcenBlockungTypes.RAUM,
          ressourceSapId: gruppenraum.raumSapId,
          start: tag.zeitraum.start,
          end: tag.zeitraum.end,
        });
      });
    });

    if (modul.seminarraum) {
      const raumSapId = modul.seminarraum.raumSapId;
      modul.tage.forEach((tag) =>
        blockungen.push({
          type: ressourcenBlockungTypes.RAUM,
          ressourceSapId: raumSapId,
          start: tag.zeitraum.start,
          end: tag.zeitraum.end,
        }),
      );
    }

    return blockungen;
  };

  return {
    akaProduktId: planResultData.akaProduktId,
    terminart: planResultData.terminart,
    modultermine: planResultData.module.map((modul) => ({
      akaModulId: getAkaModulIdByModulnummer(themaModule, modul.modulnummer),
      ablauf: modul.tage.map((t) => ({
        start: t.zeitraum.start,
        end: t.zeitraum.end,
      })),
      blockungen: getModulBlockungen(modul),
    })),
  };
};

export const getMissingGruppenraumWarnings = (
  modules: CheckDurchgangAvailabilityOutput['module'],
): PlanningWarning[] => {
  const modulNumbersWithMissingGruppenraum = modules
    .filter((module) => needsGruppenraum(module.gruppenraumInfo))
    .filter((module) => !hasGruppenraum(module.tage))
    .map((module) => module.modulnummer);

  if (modulNumbersWithMissingGruppenraum.length === 0) {
    return [];
  } else {
    return [
      {
        id: 'missing-gruppenraum-bl',
        title: 'Kein Gruppenraum ausgewählt',
        message: `Laut Planungsinformation wird bei Modul ${formatList(modulNumbersWithMissingGruppenraum)} ein Gruppenraum benötigt, aber es wurde noch keiner ausgewählt.`,
      },
    ];
  }
};

const needsGruppenraum = (gruppenraumInfo: CheckDurchgangAvailabilityOutput['module'][number]['gruppenraumInfo']) =>
  gruppenraumInfo !== null && gruppenraumInfo.trim() !== '';

const hasGruppenraum = (tage: CheckDurchgangAvailabilityOutput['module'][number]['tage']) =>
  tage.some((tag) => tag.gruppenraeume && tag.gruppenraeume.length > 0);
